import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { CurrencyIconComponent } from '../../../currency-icon/currency-icon.component';
import { IconComponent } from '../../../icon/icon.component';
import { NgClass, NgStyle } from '@angular/common';
import { SplicedBackgroundComponent } from '../../../spliced-background/spliced-background.component';
import { BaseWagerCardDirective } from '../base-wager-card.directive';
import { launchConfetti } from '../../../../features/game-result/confetti';

@Component({
  selector: 'app-wager-card',
  templateUrl: './wager-card.component.html',
  styleUrls: ['./wager-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    IconComponent,
    CurrencyIconComponent,
    ThemePipe,
    SplicedBackgroundComponent,
  ],
})
export class WagerCardComponent extends BaseWagerCardDirective {
  private readonly translocoService = inject(TranslocoService);

  @ViewChild('confettiCanvas') private confettiCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('buttonSlot') private buttonSlot?: ElementRef<HTMLElement>;

  onAnimationEnd(event: AnimationEvent): void {
    if (event.animationName.endsWith('unlock-progress-fill')) {
      const buttonRect = this.buttonSlot?.nativeElement.getBoundingClientRect();
      const canvasRect = this.confettiCanvas?.nativeElement.getBoundingClientRect();
      const origin =
        buttonRect && canvasRect
          ? {
              x: (buttonRect.left + buttonRect.width / 2 - canvasRect.left) / canvasRect.width,
              y: (buttonRect.top + buttonRect.height / 2 - canvasRect.top) / canvasRect.height,
            }
          : { x: 0.5, y: 1.0 };
      launchConfetti(this.themeService, this.confettiCanvas?.nativeElement, origin, () => {
        this.celebrationCompleteEvent.emit();
      });
      this.celebrationState.set('unlock-transition');
      return;
    }
    this.advanceCelebrationState(event.animationName);
  }

  // ─── Spliced backgrounds ───
  protected readonly splicedBg = this.themeService.splicedBackgroundPlacement('wager-card');
  protected readonly splicedPlayBg =
    this.themeService.splicedBackgroundPlacement('wager-play-button');
  protected readonly splicedLockedBg =
    this.themeService.splicedBackgroundPlacement('wager-play-button'); // TODO: Pixel Pioneers need a wager-locked-button
  protected readonly splicedInProgressBg =
    this.themeService.splicedBackgroundPlacement('wager-play-button'); // TODO: Pixel Pioneers need a wager-inprogress-button

  // ─── Model-driven styles ───
  readonly cardStyle = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    return [
      ['background', `card-wager-${lockState}`],
      ['border', `card-wager-${lockState}`],
      ['drop-shadow', 'card-wager'],
      ['border-radius', 'card-wager'],
    ];
  });

  readonly prizeStyles = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    const key = `card-wager-${lockState}-label-prize`;
    const textColorType = this.themeService.hasWagerCardGradientTextColor()
      ? 'text-color-gradient'
      : 'text-color';
    return [
      ['type', key],
      ['text-outer-border', key],
      [textColorType, key],
    ];
  });

  // ─── Style arrays ───
  readonly titleStyles = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    return [
      ['type', `card-wager-${lockState}-label-win`],
      ['text-color', `card-wager-${lockState}-label-win`],
    ];
  });

  readonly prizeShadowStyles = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    return [
      ['type', `card-wager-${lockState}-label-prize`],
      ['text-color', `card-wager-${lockState}-label-prize`],
    ];
  });

  readonly feeStyle = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    return [
      ['type', `card-wager-${lockState}-label-fee`],
      ['text-color', `card-wager-${lockState}-label-fee`],
    ];
  });

  readonly enterStyle = computed((): [string, string][] => {
    const lockState = this.effectiveLockState();
    return [
      ['type', `card-wager-${lockState}-label-enter`],
      ['text-color', `card-wager-${lockState}-label-enter`],
    ];
  });

  readonly progressCounterStyle: [string, string][] = [
    ['type', `card-wager-progress-label`],
    ['text-color', `card-wager-progress-label`],
  ];

  readonly playButtonStyles: [string, string][] = [
    ['background-gradient', 'button-wager-primary'],
    ['border-radius', 'button-wager-primary'],
    ['height', 'button-wager-primary'],
    ['border', 'button-wager-primary'],
    ['drop-and-inner-shadow', 'button-wager-primary'],
  ];

  readonly playButtonLabelStyles: [string, string][] = [
    ['type', 'button-wager-primary-label'],
    ['text-color', 'button-wager-primary-label'],
  ];

  readonly lockedButtonStyles: [string, string][] = [
    ['background-gradient', 'button-wager-locked'],
    ['border-radius', 'button-wager-locked'],
    ['height', 'button-wager-locked'],
    ['border', 'button-wager-locked'],
    ['drop-and-inner-shadow', 'button-wager-locked'],
  ];

  readonly lockedButtonLabelStyles: [string, string][] = [
    ['type', 'button-wager-locked-label'],
    ['text-color', 'button-wager-locked-label'],
  ];

  readonly inProgressButtonStyles: [string, string][] = [
    ['background-gradient', 'button-wager-inprogress'],
    ['border-radius', 'button-wager-inprogress'],
    ['height', 'button-wager-inprogress'],
    ['border', 'button-wager-inprogress'],
    ['drop-and-inner-shadow', 'button-wager-inprogress'],
  ];

  readonly inProgressButtonLabelStyles: [string, string][] = [
    ['type', 'button-wager-inprogress-label'],
    ['text-color', 'button-wager-inprogress-label'],
  ];

  readonly playerWaitingColor = '--panel-wager-tier-list-player-waiting-fill';
  readonly topPadding = 'var(--card-wager-padding-top)';
  readonly bottomPadding = 'var(--card-wager-padding-bottom)';
  readonly leftPadding = 'var(--card-wager-padding-left)';
  readonly rightPadding = 'var(--card-wager-padding-right)';

  readonly prefixLabel = this.translocoService.translate('components.wager-options.entry');
  readonly playLabel = this.translocoService.translate('components.wager-options.play');
  readonly lockedLabel = this.translocoService.translate('components.wager-options.locked');
  readonly inProgressLabel = computed(() =>
    this.winsNeeded() === 1
      ? this.translocoService.translate('components.wager-options.unlocks-win-single')
      : this.translocoService.translate('components.wager-options.unlocks-win-multiple', {
          count: this.winsNeeded(),
        }),
  );

  readonly unlockingLabel = this.translocoService.translate('components.wager-options.unlocking');

  readonly previousInProgressLabel = computed(() => {
    const winsNeeded =
      this.celebrationState() === 'unlock'
        ? this.tier.winsRequired - this.tier.previousTotalWins
        : this.winsNeeded();
    return winsNeeded === 1
      ? this.translocoService.translate('components.wager-options.unlocks-win-single')
      : this.translocoService.translate('components.wager-options.unlocks-win-multiple', {
          count: winsNeeded,
        });
  });
}
